<script setup lang="ts">
import { computed } from "vue"
import { UiAutocompleteSelect, UiButton, UiCurrencyInput, UiTextInput } from "@/components"
import type { ItemErrorResolver, SalesOrderFormState } from "./types"
import type { UiOption } from "@/types/ui"

const props = defineProps<{
  formState: SalesOrderFormState
  productOptions: UiOption[]
  itemError: ItemErrorResolver
  fieldError: (field: string) => string | undefined
}>()

const emit = defineEmits<{
  "add-item": []
  "remove-item": [index: number]
  "search-products": [query: string]
}>()

const visibleItems = computed(() => props.formState.items_attributes.filter((item) => !item._destroy))
</script>

<template>
  <section class="rounded-md border border-slate-200 p-4">
    <div class="mb-3 flex items-center justify-between">
      <h2 class="text-sm font-semibold text-slate-800">Itens do pedido</h2>
      <UiButton type="button" size="sm" variant="secondary" @click="emit('add-item')">Adicionar item</UiButton>
    </div>

    <div class="space-y-3">
      <div
        v-for="(item, index) in formState.items_attributes"
        v-show="!item._destroy"
        :key="index"
        class="grid gap-3 rounded-md border border-slate-100 p-3 md:grid-cols-4"
      >
        <UiAutocompleteSelect
          :id="`sales-order-item-product-${index}`"
          v-model="item.product_id"
          label="Produto"
          :options="productOptions"
          :error="itemError(index, 'product_id')"
          helper-text="Mostra 5 produtos iniciais; continue digitando para buscar mais"
          @search="emit('search-products', $event)"
        />

        <UiTextInput
          :id="`sales-order-item-quantity-${index}`"
          v-model="item.quantity"
          type="number"
          label="Quantidade"
          :error="itemError(index, 'quantity')"
        />

        <UiCurrencyInput
          :id="`sales-order-item-price-${index}`"
          v-model="item.unit_price_cents"
          label="Preco unitario"
          :error="itemError(index, 'unit_price')"
        />

        <div class="flex items-end">
          <UiButton type="button" size="sm" variant="danger" @click="emit('remove-item', index)">Remover</UiButton>
        </div>
      </div>

      <p v-if="visibleItems.length === 0" class="text-sm text-slate-500">Adicione ao menos um item ao pedido.</p>
      <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>
    </div>
  </section>
</template>

